import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Grid, Chip, Divider, Stack, Card, Avatar, TextField, Button, MenuItem, Select, Snackbar, Alert, IconButton, CircularProgress } from '@mui/material';
import { AccessTime, CheckCircleOutline, Description, AccountTree, Timer, DonutLarge, CalendarMonth, Flag, Person, BusinessCenter, ChatBubbleOutline, RateReview, AttachFile, OpenInNew, ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData, postData, putData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import { getStatusColor, getAvatarColor, priorityColors } from "../../../utils/statusColor";
import { formatDate } from "../../../utils/dateFormat";
import Loader from "../../../common/spinner";

function MyTaskDetails({ onClose }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const taskId = searchParams.get("taskId");
    const [taskData, setTaskData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [actualHours, setActualHours] = useState(0);
    const [actualMinutes, setActualMinutes] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const isOnHold = taskData?.status?.toLowerCase().includes('hold') || false;
    const isCompleted = taskData?.status?.toLowerCase() === 'completed';
    const isSubmitDisabled = taskData?.submit_for_review || submitting || isOnHold || isCompleted || !taskData;

    const fetchUserTaskDetails = useCallback(async () => {
        if (!taskId) return;
        try {
            setLoading(true);
            const response = await getData(API_ENDPOINTS.fetchTaskById(taskId));
            if (response.status === 'SUCCESS') {
                const taskDetail = response.result;
                if (taskDetail.assignees && Array.isArray(taskDetail.assignees)) {
                    // Using a Map to ensure unique user_ids
                    const uniqueAssignees = Array.from(
                        new Map(taskDetail.assignees.map(user => [user.user_id, user])).values()
                    );
                    taskDetail.assignees = uniqueAssignees;
                }
                setTaskData(taskDetail);
                const totalHours = taskDetail.actual_hours || 0;
                setActualHours(Math.floor(totalHours));
                setActualMinutes(Math.round((totalHours % 1) * 60));
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to load task details.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => {
        fetchUserTaskDetails();
    }, [fetchUserTaskDetails]);

    const handleTimeChange = (type, value) => {
        if (type === 'h') {
            if (value === 8) {
                // Rule: If 8 hours is selected, minutes must be 0
                setActualHours(8);
                setActualMinutes(0);
            } else {
                setActualHours(value);
            }
        } else if (type === 'm') {
            // Rule: If current hours is 8, don't allow changing minutes above 0
            if (actualHours === 8 && value > 0) {
                setSnackbar({
                    open: true,
                    message: "Total actual time cannot exceed 8 hours.",
                    severity: "warning"
                });
                setActualMinutes(0);
                return;
            }
            setActualMinutes(value);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'text/plain'];

        // 1. Filter by Type
        const validFiles = files.filter(file => allowedTypes.includes(file.type));

        if (validFiles.length !== files.length) {
            setSnackbar({ open: true, message: "Only PDF, JPG, JPEG, and TXT allowed.", severity: "error" });
        }

        setSelectedFiles(prev => {
            const newAttachments = [];

            validFiles.forEach(file => {
                const isDuplicate = prev.some(existing => existing.file_name === file.name && existing.rawFile?.size === file.size) ||
                    taskData.attachments?.some(existing => existing.file_name === file.name);

                if (!isDuplicate) {
                    newAttachments.push({
                        file_name: file.name,
                        file_url: URL.createObjectURL(file),
                        rawFile: file,
                        isLocal: true
                    });
                }
            });

            if (newAttachments.length === 0 && validFiles.length > 0) {
                setSnackbar({ open: true, message: "Selected files are already attached.", severity: "warning" });
                return prev;
            }

            return [...prev, ...newAttachments];
        });

        e.target.value = null;
    };

    const handleSubmitForReview = async () => {
        const totalActualTime = actualHours + (actualMinutes / 60);

        if (totalActualTime <= 0) {
            setSnackbar({ open: true, message: "Please provide actual hours before submitting.", severity: "error" });
            return;
        }

        try {
            setSubmitting(true);
            const currentTaskId = taskId || taskData?.id;
            let attachmentResponse = null;

            // 1. Logic: If files exist, call Upload API first
            if (selectedFiles.length > 0) {
                const fileFormData = new FormData();
                fileFormData.append("task_id", currentTaskId);
                selectedFiles.forEach((fileObj) => {
                    if (fileObj.rawFile) {
                        fileFormData.append("files", fileObj.rawFile); // Key matches your CURL: "files"
                    }
                });

                // Using postData (assuming your instance has it) for multipart/form-data
                attachmentResponse = await postData(API_ENDPOINTS.uploadTaskAttachments, fileFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (attachmentResponse.status !== 'SUCCESS') {
                    throw new Error("File upload failed. Task update cancelled.");
                }
            }

            // 2. Prepare Task Update Data
            // Based on your backend, it expects JSON, not FormData for updateTask
            const updatePayload = {
                id: currentTaskId,
                task_title: taskData.task_title,
                status: taskData.status,
                priority: taskData.priority,
                progress: taskData.progress,
                actual_hours: totalActualTime,
                estimated_hours: taskData.estimated_hours || 0,
                submit_for_review: true,
                user_id: userId,
                user_type: userData?.user_type || "Employee",
                assignees: taskData.assignees?.map(a => a.id || a.user_id) || [],
                comments: commentText.trim() ? [commentText.trim()] : []
            };

            // 3. Logic: Always call update if fields are changed or if file upload succeeded
            // In your case, you want to send for review, so update is always called.
            const updateUrl = API_ENDPOINTS.updateUserTaskById; // No ID in URL based on your router.put('/updateTask', ...)

            const updateResponse = await putData(updateUrl, updatePayload);

            if (updateResponse.status === 'SUCCESS') {
                setSnackbar({ open: true, message: "Task and attachments updated successfully!", severity: "success" });

                // Reset states
                setCommentText("");
                setSelectedFiles([]);
                await fetchUserTaskDetails();

                if (onClose) setTimeout(onClose, 2000);
            } else {
                throw new Error(updateResponse.message || "Failed to update task details.");
            }

        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || error.message || "Operation failed.",
                severity: "error"
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><Loader /></Box>;
    if (!taskData) return <Typography color="error" sx={{ p: 4 }}>Task details could not be loaded.</Typography>;

    const allAttachments = [...(taskData.attachments || []), ...selectedFiles];

    return (
        <Box>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', mb: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: '#64748b', borderRadius: '0px', p: 1, '&:hover': { bgcolor: '#e0e3e6' } }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: 1 }}>
                    <Typography variant="h6" fontWeight="bold">User Task Details</Typography>

                    <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={isSubmitDisabled}
                            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <RateReview />}
                            onClick={handleSubmitForReview}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.2,
                                px: 3,
                                bgcolor: (isOnHold || isCompleted) ? '#94a3b8' : '#3b82f6'
                            }}
                        >
                            {isOnHold ? "Task on Hold" : (taskData.submit_for_review || isCompleted ? "Submitted for Review" : "Send for Review")}
                        </Button>
                    </Stack>
                </Box>
            </Stack>

            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.txt" multiple onChange={handleFileChange} />

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8.5 }}>
                    <Stack spacing={3}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            <span style={{ color: '#94a3b8', marginRight: '8px' }}>#{taskData.task_id}</span>
                            {taskData.task_title}
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                { icon: <BusinessCenter fontSize="small" />, label: 'Project', val: taskData.Project?.project_title },
                                { icon: <Person fontSize="small" />, label: 'Created By', val: `${taskData.manager?.first_name} ${taskData.manager?.last_name}` },
                                { icon: <AccountTree fontSize="small" />, label: 'Task Type', val: taskData.task_type },
                                { icon: <AccessTime fontSize="small" />, label: 'Work Type', val: taskData.work_type },
                                { icon: <CalendarMonth fontSize="small" />, label: 'Timeline', val: `${new Date(taskData.start_date).toLocaleDateString()} - ${new Date(taskData.due_date).toLocaleDateString()}` },
                                { icon: <Timer fontSize="small" />, label: 'Estimated', val: `${taskData.estimated_hours || 0} hrs` },
                                { icon: <Flag fontSize="small" />, label: 'Priority', val: taskData.priority, isChip: true, chipColors: { bg: priorityColors[taskData.priority]?.bg || '#fff7ed', text: priorityColors[taskData.priority]?.color || '#c2410c', border: `1px solid ${priorityColors[taskData.priority]?.border || '#fcd34d'}` } },
                                { icon: <DonutLarge fontSize="small" />, label: 'Status', val: taskData.status, isChip: true, chipColors: getStatusColor(taskData.status) },
                            ].map((item, idx) => (
                                <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{ color: '#94a3b8', display: 'flex' }}>{item.icon}</Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', minWidth: 100 }}>{item.label}</Typography>
                                        {item.isChip ? (
                                            <Chip
                                                label={item.val}
                                                size="small"
                                                sx={{
                                                    bgcolor: item.chipColors?.bg,
                                                    color: item.chipColors?.text,
                                                    fontWeight: 600,
                                                    px: 1,
                                                    border: item.chipColors?.border || `1px solid ${item.chipColors?.color || item.chipColors?.text || '#cbd5e1'}40`
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.val}</Typography>
                                        )}
                                    </Stack>
                                </Grid>
                            ))}

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{ color: '#94a3b8', display: 'flex' }}><CheckCircleOutline fontSize="small" /></Box>
                                    <Typography variant="body2" sx={{ color: '#64748b', minWidth: 100 }}>Actual Time</Typography>
                                    {(!taskData.submit_for_review && !isOnHold && !isCompleted) ? (
                                        <Stack direction="row" spacing={1}>
                                            <Select
                                                size="small"
                                                value={actualHours}
                                                onChange={(e) => handleTimeChange('h', e.target.value)}
                                                sx={{ height: 32, width: 70 }}
                                            >
                                                {[...Array(9).keys()].map(h => (
                                                    <MenuItem key={h} value={h}>{h}h</MenuItem>
                                                ))}
                                            </Select>
                                            <Select
                                                size="small"
                                                value={actualMinutes}
                                                onChange={(e) => handleTimeChange('m', e.target.value)}
                                                disabled={actualHours === 8} // Disable minutes if hours is 8
                                                sx={{
                                                    height: 32,
                                                    width: 80,
                                                    bgcolor: actualHours === 8 ? '#f1f5f9' : 'transparent'
                                                }}
                                            >
                                                {[0, 15, 30, 45].map(m => (
                                                    <MenuItem
                                                        key={m}
                                                        value={m}
                                                        disabled={actualHours === 8 && m > 0} // Visual safeguard
                                                    >
                                                        {m}m
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Stack>
                                    ) : (
                                        // Shows static text instead of inputs when on hold or submitted
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {actualHours}h {actualMinutes}m
                                        </Typography>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', display: 'flex', gap: 1 }}>
                                <Description sx={{ color: '#0369a1', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0369a1' }}>Description</Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{taskData.description || "No description provided."}</Typography>
                            </Box>
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', display: 'flex', gap: 1 }}>
                                <AttachFile sx={{ color: '#0369a1', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0369a1' }}>Attachments ({allAttachments.length})</Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                {allAttachments.length > 0 ? (
                                    <Grid container spacing={2}>
                                        {allAttachments.map((file, index) => (
                                            <Grid size={{ xs: 12 }} key={index}>
                                                <Box component="a" href={file.file_url} target="_blank" rel="noopener noreferrer"
                                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, textDecoration: 'none', color: 'inherit' }}>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Avatar sx={{ bgcolor: file.isLocal ? '#fff7ed' : '#eff6ff', color: file.isLocal ? '#c2410c' : '#3b82f6', width: 32, height: 32 }}><Description fontSize="small" /></Avatar>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{file.file_name} {file.isLocal && "(Pending)"}</Typography>
                                                    </Stack>
                                                    <OpenInNew sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No attachments found.</Typography>}
                            </Box>
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 1 }}>
                                <ChatBubbleOutline sx={{ color: '#64748b' }} />
                                <Typography variant="subtitle2">Comments ({taskData.comments?.length || 0})</Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                <Stack spacing={3} sx={{ mb: 2 }}>
                                    {taskData.comments?.map((c, i) => (
                                        <Stack key={i} direction="row" spacing={2}>
                                            <Box sx={{ width: 42, height: 42, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569' }}>
                                                    {/* Ensures both initials are capitalized */}
                                                    {`${c?.first_name?.charAt(0) || ''}${c?.last_name?.charAt(0) || ''}`.toUpperCase()}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        {c?.first_name} {c?.last_name}
                                                        <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.7rem', ml: 1 }}>• {c.user_type}</Typography>
                                                    </Typography>
                                                    <Typography variant="caption">{formatDate(c.createdAt)}</Typography>
                                                </Box>
                                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '0 12px 12px 12px', mt: 0.5 }}>
                                                    <Typography variant="body2">{c.comment}</Typography>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>

                                {isOnHold ? (
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        Comments are disabled while the task is on hold.
                                    </Alert>
                                ) : (taskData.submit_for_review || isCompleted) ? ( // Added isCompleted check
                                    <Alert severity="success" icon={<CheckCircleOutline fontSize="inherit" />} sx={{ borderRadius: 2 }}>
                                        {isCompleted ? "This task is completed. Further comments are restricted." : "This task has been submitted for review. Further comments are restricted."}
                                    </Alert>
                                ) : (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={commentText}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCommentText(value.charAt(0).toUpperCase() + value.slice(1));
                                        }}
                                        placeholder="Write a comment..."
                                    />
                                )}
                            </Box>
                        </Card>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 3.5 }} >
                    <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="textSecondary" fontWeight={700}>PROGRESS</Typography>
                            <Typography variant="h6" fontWeight={700}>{taskData.progress}%</Typography>
                        </Box>
                        <Box sx={{ width: '100%', bgcolor: '#e2e8f0', borderRadius: 10, height: 8, mt: 1 }}>
                            <Box sx={{ width: `${taskData.progress}%`, bgcolor: '#3b82f6', borderRadius: 10, height: '100%' }} />
                        </Box>
                        <Divider sx={{ my: 2.5 }} />
                        <Typography variant="caption" color="textSecondary" fontWeight={700} sx={{ display: 'block', mb: 2 }}>ASSIGNED TO</Typography>
                        <Stack spacing={2}>
                            {Array.from(new Map(taskData.assignees?.map(p => [p.user_id, p])).values()).map((p, i) => (
                                <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            bgcolor: getAvatarColor(p.first_name),
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                            {`${p?.first_name?.charAt(0) || ''}${p?.last_name?.charAt(0) || ''}`.toUpperCase()}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                        {p.first_name} {p.last_name}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Card>

                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <Button fullWidth variant="outlined" startIcon={<AttachFile />} onClick={() => fileInputRef.current.click()} disabled={taskData.submit_for_review || isOnHold || isCompleted} sx={{ borderRadius: '10px', textTransform: 'none' }}>
                            Add Attachments
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default MyTaskDetails;